import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import Botonera from "./Botonera";
import { MailDialog } from "./dialogs/MailDialog";
import { HistoriaDialog } from "./dialogs/HistoriaDialog";
import { FasesDialog } from "./dialogs/FasesDialog";
import { EstadosDialog } from "./dialogs/EstadosDialog";
import { EditarCotizacionDialog } from "./dialogs/EditarCotizacion";
import DatosDialog from "../../clientes/components/dialogs/DatosDialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { parseCustomDate, formatDate } from "../../../utilities/DateUtil";
import {
  abrirCotizacionPDF,
  getReporteCotizacionHistoria,
  revisarCotizacion,
  descartarCotizacion,
  descartarCotizacionBloqueada,
  recotizarCotizacion,
  validarCotizacion,
  finalizarCotizacion,
  getReporteSelectCotizacion,
  cambiarFechaDecision,
  cambiarFechaEntrega,
  enviarCotizacion,
  getFaseCotizacion,
  cambiarEstadosCotizacion,
  getCausaCotizacion,
} from "../../../service/AffariService";
import { useGlobalContext } from "../../../Config";

const documentoMap = {
  ZSDE: "Factura",
  ZSME: "Guía / Factura",
  ZFME: "Venta Calzada",
  ZVTS: "Servicio Sack",
  ZVFX: "Exportación",
  ZBOE: "Boleta",
};

const DateCell = ({ value, label, onChange }) => {
  const [date, setDate] = useState(null);

  useEffect(() => {
    setDate(parseCustomDate(value));
  }, [value]);

  const handleChange = (e) => {
    setDate(e.value);
    if (onChange) {
      onChange(e.value);
    }
  };

  return (
    <div className="flex align-items-center gap-2">
      <span className="date-cell-label">{label}</span>
      <Calendar
        value={date}
        onChange={handleChange}
        showIcon
        dateFormat="dd/mm/yy"
        inputClassName="p-inputtext-sm"
        className="date-cell-calendar w-9rem h-2rem"
        icon="pi pi-pencil"
      />
    </div>
  );
};

const MotivoCell = ({
  rowData,
  causas,
  glosacausas,
  setLoading,
  toast,
  onRefresh,
}) => {
  const [causaId, setCausaId] = useState(null);
  const [glosaId, setGlosaId] = useState(null);

  useEffect(() => {
    const resolvedCausaId = rowData?.causa?.id ?? rowData?.causa ?? null;
    const resolvedGlosaId =
      rowData?.glosaCausa?.id ?? rowData?.glosaCausa ?? null;

    setCausaId(resolvedCausaId ?? null);
    setGlosaId(resolvedGlosaId ?? null);
  }, [rowData?.causa, rowData?.glosaCausa]);

  const causasOptions = (() => {
    if (!Array.isArray(causas)) return [];
    return causas;
  })();

  const glosaOptions = (() => {
    if (!glosacausas || !causaId) return [];
    const list = glosacausas[causaId];
    return Array.isArray(list) ? list : [];
  })();

  return (
    <div
      className="flex flex-column gap-1"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Dropdown
        value={causaId}
        options={causasOptions}
        optionLabel="descripcion"
        optionValue="id"
        className="p-inputtext-sm w-10rem h-2rem"
        style={{ maxWidth: "10rem" }}
        onChange={async (e) => {
          const nextCausaId = e.value;
          setCausaId(nextCausaId);
          setGlosaId(null);

          const idCotizacion = rowData?.id;
          if (!idCotizacion || !nextCausaId) return;

          try {
            if (setLoading) setLoading(true);
            await getCausaCotizacion(idCotizacion, nextCausaId);
            // await getBotonCausaCotizacion(idCotizacion);
            if (toast?.current) {
              toast.current.show({
                severity: "success",
                summary: "Éxito",
                detail: "Motivo actualizado correctamente",
              });
            }
            if (onRefresh) onRefresh();
          } catch (error) {
            console.error("Error actualizando motivo de cotización:", error);
            if (toast?.current) {
              toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Error al actualizar el motivo",
              });
            }
          } finally {
            if (setLoading) setLoading(false);
          }
        }}
      />
      {Array.isArray(glosaOptions) && glosaOptions.length > 0 && (
        <Dropdown
          value={glosaId}
          options={glosaOptions}
          optionLabel="glosa"
          optionValue="id"
          className="p-inputtext-sm w-10rem h-2rem"
          style={{ maxWidth: "10rem" }}
          onChange={async (e) => {
            const nextGlosaId = e.value;
            setGlosaId(nextGlosaId);

            const idCotizacion = rowData?.id;
            const currentCausaId =
              causaId ?? rowData?.causa?.id ?? rowData?.causa;
            if (!idCotizacion || !currentCausaId) return;

            try {
              if (setLoading) setLoading(true);
              await getCausaCotizacion(idCotizacion, currentCausaId);
              // await getBotonCausaCotizacion(idCotizacion);
              if (toast?.current) {
                toast.current.show({
                  severity: "success",
                  summary: "Éxito",
                  detail: "Motivo actualizado correctamente",
                });
              }
              if (onRefresh) onRefresh();
            } catch (error) {
              console.error("Error actualizando motivo de cotización:", error);
              if (toast?.current) {
                toast.current.show({
                  severity: "error",
                  summary: "Error",
                  detail: "Error al actualizar el motivo",
                });
              }
            } finally {
              if (setLoading) setLoading(false);
            }
          }}
        />
      )}
    </div>
  );
};

const Tabla = ({
  setLoading,
  cotizaciones,
  estados,
  glosacausas,
  competencias,
  causas,
  toast,
  onRefresh,
  variant,
}) => {
  const { isSack, isDinar } = useGlobalContext();
  const navigate = useNavigate();
  const [visibleHistoria, setVisibleHistoria] = useState(false);
  const [historiaData, setHistoriaData] = useState([]);
  const [visibleEmail, setVisibleEmail] = useState(false);
  const [visibleFases, setVisibleFases] = useState(false);
  const [fasesData, setFasesData] = useState({
    cotizacion: null,
    detalles: [],
    map: {},
  });
  const [isDatosDialogVisible, setIsDatosDialogVisible] = useState(false);
  const [selectedClienteCodigo, setSelectedClienteCodigo] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  const [visibleCount, setVisibleCount] = useState(10);
  const selectedCotizaciones = useState(null);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState(null);
  const [visibleEstados, setVisibleEstados] = useState(false);
  const [estadosData, setEstadosData] = useState({
    cotizacion: null,
    estados: [],
    detalles: [],
  });
  const [visibleEditar, setVisibleEditar] = useState(false);
  const [editarData, setEditarData] = useState({
    cotizacion: null,
    detalles: [],
  });
  const loadMoreRef = useRef(null);
  const [cotizacionData, setCotizacionData] = useState(null);
  const filteredCotizaciones = Array.isArray(cotizaciones)
    ? cotizaciones.filter(
        (item) => item?.hijo !== true && item?.hijo !== "true",
      )
    : [];

  const variantConfig = (() => {
    switch (variant) {
      case "COTIZACION":
        return { key: "COTIZACION" };
      case "NOTAVENTA":
        return { key: "NOTAVENTA" };
      case "BLOQUEADA":
        return { key: "BLOQUEADA" };
      case "NOCONFIRMADOS":
        return { key: "NOCONFIRMADOS" };
      case "TIEMPO":
        return { key: "TIEMPO" };
      default:
        return null;
    }
  })();

  const isSupportedVariant = Boolean(variantConfig);

  const columnFlags = {
    sackCotizacion: isSack && variantConfig?.key === "COTIZACION",
    dinarCotizacion: isDinar && variantConfig?.key === "COTIZACION",
    dinarNotaVenta: isDinar && variantConfig?.key === "NOTAVENTA",
    dinarBloqueada: isDinar && variantConfig?.key === "BLOQUEADA",
    noConfirmados: variantConfig?.key === "NOCONFIRMADOS",
    tiempo: variantConfig?.key === "TIEMPO",
    showTotal: !["NOTAVENTA", "TIEMPO", "NOCONFIRMADOS"].includes(
      variantConfig?.key,
    ),
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [variant]);

  useEffect(() => {
    if (isMobile) {
      setVisibleCount(10);
    }
  }, [isMobile, filteredCotizaciones.length]);

  useEffect(() => {
    if (!isMobile) return;
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + 10, filteredCotizaciones.length),
          );
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile, filteredCotizaciones.length]);

  const handleClienteClick = (codigo) => {
    setSelectedClienteCodigo(codigo);
    setIsDatosDialogVisible(true);
  };

  const onEditar = async (rowData) => {
    try {
      const response = await getReporteSelectCotizacion(rowData.id);
      setEditarData(response);
      setVisibleEditar(true);
    } catch (error) {
      console.error("Error fetching cotización para editar:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error al cargar cotización para editar",
      });
    }
  };

  const onDescartar = async (rowData) => {
    if (
      window.confirm(
        `¿Está seguro de que desea descartar la cotización ${rowData.folio}?`,
      )
    ) {
      try {
        setLoading(true);

        if (variantConfig?.key === "BLOQUEADA") {
          await descartarCotizacionBloqueada(rowData.id);
        } else {
          await descartarCotizacion(rowData.id);
        }

        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Cotización descartada correctamente",
        });
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error("Error descartando cotización:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error?.message || "Error al descartar la cotización",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const onFinalizar = async (rowData) => {
    if (
      window.confirm(
        `¿Está seguro de que desea finalizar la cotización ${rowData.folio}?`,
      )
    ) {
      try {
        setLoading(true);
        await validarCotizacion(rowData.id);
        await finalizarCotizacion(rowData.id, 0, "");
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Cotización finalizada correctamente",
        });
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error("Error finalizando cotización:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Error al finalizar la cotización",
        });
      }
    }
  };

  const onVer = (rowData) => {
    abrirCotizacionPDF(rowData.id);
  };

  const onValidar = async (rowData) => {
    if (
      !window.confirm(
        `¿Está seguro de que desea validar la cotización ${rowData.folio}?`,
      )
    )
      return;

    try {
      setLoading(true);
      const validacion = await validarCotizacion(rowData.id);

      if (validacion?.nivel !== "OK") {
        toast.current.show({
          severity: validacion?.nivel === "WARNING" ? "warn" : "error",
          summary: "Validación",
          detail: validacion?.mensaje || "La cotización no pudo ser validada",
        });
        return;
      }

      if (variantConfig?.key === "BLOQUEADA") {
        const recotizacion = await recotizarCotizacion(rowData.id);

        if (recotizacion?.nivel !== "OK") {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              recotizacion?.mensaje ||
              "La cotización fue validada, pero no pudo ser recotizada",
          });
          return;
        }

        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Cotización desbloqueada y recotizada correctamente",
        });
      } else {
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Cotización validada correctamente",
        });
      }

      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error validando cotización:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error al validar la cotización",
      });
    } finally {
      setLoading(false);
    }
  };

  const getActionHandlers = (rowData) => {
    if (variantConfig?.key === "BLOQUEADA") {
      return { onVer, onValidar, onDescartar };
    }

    if (variantConfig?.key === "COTIZACION") {
      return { onVer, onEditar, onDescartar };
    }

    if (rowData.borrador) {
      return { onVer, onEditar, onDescartar, onFinalizar, onValidar };
    }

    if (rowData.compra) {
      return {
        onVer,
        onMail,
        onHistoria,
        onRecotizar,
        onFases,
        onPublicar,
        onValidar,
      };
    }

    return { onVer, onMail, onHistoria, onRecotizar, onFases };
  };

  const onMail = async (rowData) => {
    try {
      setLoading(true);
      await enviarCotizacion(rowData.id);
      toast.current.show({
        severity: "success",
        summary: "Éxito",
        detail: "Cotización enviada por correo correctamente",
      });
    } catch (error) {
      console.error("Error enviando cotización:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error al enviar la cotización por correo",
      });
    } finally {
      setLoading(false);
    }
  };

  const onHistoria = async (rowData) => {
    try {
      const response = await getReporteCotizacionHistoria(rowData.id);
      setHistoriaData(response.historia || []);
      setVisibleHistoria(true);
    } catch (error) {
      console.error("Error fetching historia:", error);
    }
  };

  const onRecotizar = async (rowData) => {
    if (
      window.confirm(
        `¿Está seguro de que desea recotizar la cotización ${rowData.folio}?`,
      )
    ) {
      try {
        await recotizarCotizacion(rowData.id);
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Cotización recotizada correctamente",
        });
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error("Error recotizando:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Error al recotizar",
        });
      }
    }
  };

  const onFases = async (rowData) => {
    try {
      const response = await getReporteSelectCotizacion(rowData.id);
      setCotizacionData(response.cotizacion || {});
      const data = await getFaseCotizacion(rowData.id);
      setFasesData(data);
      setVisibleFases(true);
    } catch (error) {
      console.error("Error fetching cotización para editar:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error al cargar cotización para editar",
      });
    }
  };

  const onEstados = async (rowData) => {
    try {
      const response = await getReporteSelectCotizacion(rowData.id);
      setEstadosData(response);
      setVisibleEstados(true);
    } catch (error) {
      console.error("Error fetching estados:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error al cargar estados",
      });
    }
  };

  const onPublicar = async (rowData) => {
    if (
      window.confirm(
        `¿Está seguro de que desea publicar la cotización ${rowData.folio}?`,
      )
    ) {
      try {
        setLoading(true);
        await revisarCotizacion(rowData.id);
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Cotización publicada correctamente",
        });
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error("Error publicando cotización:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Error al publicar la cotización",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDateForApi = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const onChangeFechaDecision = async (rowData, date) => {
    try {
      setLoading(true);
      const fecha = formatDateForApi(date);
      await cambiarFechaDecision(rowData.id, fecha);
      toast.current.show({
        severity: "success",
        summary: "Éxito",
        detail: "Fecha de decisión actualizada correctamente",
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error cambiando fecha de decisión:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error al cambiar la fecha de decisión",
      });
    } finally {
      setLoading(false);
    }
  };

  const onChangeFechaEntrega = async (rowData, date) => {
    try {
      setLoading(true);
      const fecha = formatDateForApi(date);
      await cambiarFechaEntrega(rowData.id, fecha);
      toast.current.show({
        severity: "success",
        summary: "Éxito",
        detail: "Fecha de entrega actualizada correctamente",
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error cambiando fecha de entrega:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Error al cambiar la fecha de entrega",
      });
    } finally {
      setLoading(false);
    }
  };

  const rowClassName = (rowData) => {
    if (!isSack) return "";
    return rowData.borrador ? "bg-yellow-500" : "";
  };

  const visibleData = filteredCotizaciones.slice(0, visibleCount);

  if (!isSupportedVariant) return null;

  return (
    <>
      <div className="flex flex-column h-full">
        {isMobile ? (
          <div className="flex flex-column gap-3 flex-1 overflow-y-auto">
            {filteredCotizaciones.length > 0 ? (
              <>
                {visibleData.map((rowData, index) => (
                  <div
                    key={rowData.id ?? rowData.folio ?? index}
                    className={`${
                      !rowData.borrador ? "surface-card" : ""
                    } border-1 surface-border border-round p-3 shadow-1 ${rowClassName(
                      rowData,
                    )}`}
                  >
                    <div className="flex justify-content-between align-items-center mb-2">
                      <div className="flex align-items-center gap-2">
                        <span className="text-500 text-sm">#{index + 1}</span>
                        <span className="font-bold text-900">
                          Folio: {rowData.folio}
                        </span>
                      </div>
                      <Botonera
                        rowData={rowData}
                        {...getActionHandlers(rowData)}
                        variant={variant}
                        isDinar={isDinar}
                        isSack={isSack}
                      />
                    </div>
                    <div className="flex flex-column gap-2">
                      <div className="flex flex-column">
                        <span className="text-600 text-sm">Cliente</span>
                        <span
                          className="font-medium text-900 cursor-pointer hover:underline"
                          onClick={() => handleClienteClick(rowData.codigo)}
                        >
                          {rowData.codigo} - {rowData.razonSocial}
                        </span>
                      </div>
                      <div className="grid grid-nogutter gap-2">
                        <div className="col-6">
                          <div className="text-600 text-sm">RUT</div>
                          <div className="text-900">{rowData.rut}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-600 text-sm">Total</div>
                          <div className="text-900 font-bold">
                            ${rowData.total}
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="text-600 text-sm">Fecha</div>
                          <div className="text-900">
                            {formatDate(rowData.fecha)}
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="text-600 text-sm">Estado</div>
                          <div className="text-900">{rowData.estado}</div>
                        </div>
                      </div>
                      {rowData.documento === "ZBOE" && (
                        <div className="flex flex-column gap-1 mt-1 border-top-1 surface-border pt-2">
                          <span className="text-sm">
                            <b>Cliente Boleta:</b> {rowData.nombre}
                          </span>
                          <span className="text-sm">
                            <b>Tel:</b> {rowData.telefono}
                          </span>
                          <span className="text-sm">
                            <b>Email:</b> {rowData.correo}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {visibleCount < filteredCotizaciones.length ? (
                  <div
                    ref={loadMoreRef}
                    className="flex justify-content-center align-items-center p-3 text-600"
                  >
                    Cargando más...
                  </div>
                ) : null}
              </>
            ) : (
              <div className="surface-card border-1 surface-border border-round p-3 text-center text-600">
                No hay ningún registro que mostrar
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            <DataTable
              value={filteredCotizaciones}
              rowClassName={rowClassName}
              tableStyle={{ minWidth: "50rem" }}
              size="small"
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 50, 100]}
              dataKey="id"
              className="p-datatable-sm border-1 surface-border border-round"
              globalFilterFields={["cliente.razonSocial"]}
              stripedRows
              rowHover
              showGridlines
              sortMode="multiple"
              removableSort
              resizableColumns
              columnResizeMode="fit"
              reorderableColumns
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
              currentPageReportTemplate="{first} - {last} de {totalRecords} cotizaciones"
              emptyMessage={
                <div className="p-4 text-center text-600">
                  No hay ningún registro que mostrar
                </div>
              }
              selection={selectedCotizaciones}
              scrollable
              scrollHeight="flex"
              style={{ height: "100%" }}
            >
              {columnFlags.sackCotizacion && (
                <Column
                  columnKey="index"
                  selectionMode="multiple"
                  headerStyle={{ width: "3rem" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                />
              )}

              {!columnFlags.noConfirmados && (
                <Column
                  columnKey="acciones"
                  header={columnFlags.dinarBloqueada ? "Acción" : "#"}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "6%" }}
                  body={(rowData) => (
                    <Botonera
                      rowData={rowData}
                      {...getActionHandlers(rowData)}
                      variant={variant}
                      isDinar={isDinar}
                      isSack={isSack}
                    />
                  )}
                />
              )}

              {columnFlags.sackCotizacion && (
                <Column
                  columnKey="origen"
                  header="Origen"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "5%" }}
                  body={(rowData) => (
                    <div className="flex flex-column">
                      <span>
                        {rowData.origen === "PLANIFICADOR"
                          ? "PC"
                          : rowData.origen}
                      </span>
                      <span>{rowData.tipo}</span>
                      <span>
                        {documentoMap[rowData.documento] || rowData.documento}
                      </span>
                    </div>
                  )}
                />
              )}

              {!columnFlags.noConfirmados && (
                <Column
                  columnKey="folio"
                  header="Folio"
                  style={{ width: "5%" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  body={(rowData) =>
                    columnFlags.dinarBloqueada ? (
                      rowData.folio
                    ) : (
                      <div className="flex flex-column align-items-end">
                        <span>
                          <b>Folio</b>
                        </span>
                        {rowData.folio}
                        <br />
                        <span>
                          <b>ID</b>
                        </span>
                        {rowData.id}
                      </div>
                    )
                  }
                />
              )}

              {columnFlags.dinarBloqueada && (
                <Column
                  columnKey="fecha"
                  header="Fecha"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "5%" }}
                />
              )}

              {(columnFlags.dinarNotaVenta || columnFlags.tiempo) && (
                <Column
                  columnKey="notaVenta"
                  header="Nota de Venta"
                  style={{ width: "5%" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  body={(rowData) =>
                    rowData.notaVenta ?? rowData.nota_venta ?? rowData.nv ?? ""
                  }
                />
              )}

              {columnFlags.dinarNotaVenta && (
                <Column
                  columnKey="factura"
                  header="Factura"
                  style={{ width: "5%" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                />
              )}

              {columnFlags.sackCotizacion && (
                <Column
                  columnKey="pedido"
                  header="Pedido"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "5%", maxWidth: "6rem" }}
                  body={(rowData) => (
                    <div className="flex flex-column">
                      <span
                        className="white-space-nowrap overflow-hidden text-overflow-ellipsis"
                        style={{ maxWidth: "5rem" }}
                        title={rowData.ordenVentas}
                      >
                        {rowData.ordenVentas}
                      </span>
                    </div>
                  )}
                />
              )}

              {!columnFlags.noConfirmados && (
                <Column
                  columnKey="cliente"
                  header="Cliente"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "39%" }}
                  body={(rowData) => (
                    <div className="flex flex-column">
                      <div>
                        <span
                          className="text-primary cursor-pointer hover:underline font-bold"
                          onClick={() => handleClienteClick(rowData.codigo)}
                        >
                          {rowData.codigo}
                        </span>
                        <Button
                          icon="pi pi-plus"
                          tooltip="Crear"
                          tooltipOptions={{ position: "top" }}
                          text
                          className="p-button-sm p-0 w-1rem ml-2"
                          onClick={() => {
                            if (rowData.documento === "ZBOE") {
                              if (
                                window.confirm(
                                  "¿Está seguro de que desea ingresar una cotización boleta?",
                                )
                              ) {
                                navigate("/boleta");
                              }
                            } else {
                              if (
                                window.confirm(
                                  "¿Está seguro de que desea ingresar una cotización factura?",
                                )
                              ) {
                                navigate("/factura");
                              }
                            }
                          }}
                        />
                        <span className="ml-2">
                          {rowData.rut} - {rowData.razonSocial}
                        </span>
                      </div>
                      <span>
                        VENDEDOR: <b>{rowData.vendedor}</b>{" "}
                      </span>
                      <span>
                        LISTA PRECIO: <b>{rowData.listaPrecio}</b>{" "}
                      </span>
                      <br />
                      {rowData.documento === "ZBOE" && (
                        <>
                          <span>Cliente Boleta {rowData.nombre}</span>
                          <span>Telefono {rowData.telefono}</span>
                          <span>E-mail {rowData.correo}</span>
                        </>
                      )}
                    </div>
                  )}
                />
              )}

              {columnFlags.noConfirmados && (
                <Column
                  columnKey="codigoNoConfirmado"
                  header="Código"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "6%" }}
                  body={(rowData) =>
                    rowData.codigoProducto ??
                    rowData.codigoItem ??
                    rowData.codigo_material ??
                    rowData.codigo ??
                    ""
                  }
                />
              )}

              {columnFlags.noConfirmados && (
                <Column
                  columnKey="descripcionNoConfirmado"
                  header="Descripción"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "14%" }}
                  body={(rowData) =>
                    rowData.descripcionProducto ??
                    rowData.descripcionItem ??
                    rowData.material ??
                    rowData.descripcion ??
                    ""
                  }
                />
              )}

              {columnFlags.noConfirmados && (
                <Column
                  columnKey="solicitadoNoConfirmado"
                  header="Solicitado"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "6%" }}
                  body={(rowData) =>
                    rowData.solicitado ??
                    rowData.cantidadSolicitada ??
                    rowData.cantidad_solicitada ??
                    ""
                  }
                />
              )}

              {columnFlags.noConfirmados && (
                <Column
                  columnKey="confirmadoNoConfirmado"
                  header="Confirmado"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "6%" }}
                  body={(rowData) =>
                    rowData.confirmado ??
                    rowData.cantidadConfirmada ??
                    rowData.cantidad_confirmada ??
                    ""
                  }
                />
              )}

              {columnFlags.noConfirmados && (
                <Column
                  columnKey="diferenciaNoConfirmado"
                  header="Diferencia"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "6%" }}
                  body={(rowData) => {
                    const diferencia =
                      rowData.diferencia ??
                      rowData.cantidadDiferencia ??
                      rowData.cantidad_diferencia;
                    if (diferencia != null) return diferencia;
                    const solicitado = Number(
                      rowData.solicitado ??
                        rowData.cantidadSolicitada ??
                        rowData.cantidad_solicitada,
                    );
                    const confirmado = Number(
                      rowData.confirmado ??
                        rowData.cantidadConfirmada ??
                        rowData.cantidad_confirmada,
                    );
                    return Number.isFinite(solicitado) &&
                      Number.isFinite(confirmado)
                      ? solicitado - confirmado
                      : "";
                  }}
                />
              )}

              {(columnFlags.dinarCotizacion ||
                columnFlags.dinarNotaVenta ||
                columnFlags.dinarBloqueada) && (
                <Column
                  columnKey="entrega"
                  header="Entrega"
                  style={{ width: "5%" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                />
              )}
              {(columnFlags.dinarCotizacion || columnFlags.dinarBloqueada) && (
                <Column
                  columnKey="pagado"
                  header="Pagado"
                  style={{ width: "5%" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  body={(rowData) => {
                    const pagado =
                      rowData.pagado === true ||
                      rowData.pagado === 1 ||
                      rowData.pagado === "1" ||
                      rowData.pagado === "true";

                    return (
                      <div className="flex justify-content-center">
                        <i
                          className={pagado ? "pi pi-check" : "pi pi-times"}
                          style={{
                            fontSize: "1.2rem",
                            color: pagado ? "#16a34a" : "#dc2626",
                            fontWeight: "bold",
                          }}
                          title={pagado ? "Pagado" : "No pagado"}
                        />
                      </div>
                    );
                  }}
                />
              )}

              {columnFlags.dinarNotaVenta && (
                <Column
                  columnKey="pendiente"
                  header="Pendiente"
                  style={{ width: "5%" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                />
              )}

              {(columnFlags.dinarCotizacion ||
                columnFlags.dinarNotaVenta ||
                columnFlags.dinarBloqueada) && (
                <Column
                  columnKey="usuario"
                  header="Usuario"
                  style={{ width: "5%" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                />
              )}

              {columnFlags.sackCotizacion && (
                <Column
                  columnKey="fechas"
                  header="Fechas"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "10%" }}
                  body={(rowData) => (
                    <div className="flex flex-column gap-2">
                      <span>Cotización: {formatDate(rowData.fecha)}</span>
                      <DateCell
                        value={rowData.decision}
                        label="Decisión:"
                        onChange={(date) =>
                          onChangeFechaDecision(rowData, date)
                        }
                      />
                      <DateCell
                        value={rowData.entrega}
                        label="Entrega:"
                        onChange={(date) => onChangeFechaEntrega(rowData, date)}
                      />
                    </div>
                  )}
                />
              )}

              {(columnFlags.dinarNotaVenta || columnFlags.dinarCotizacion) && (
                <Column
                  columnKey="fecha"
                  header="Fecha"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "5%" }}
                />
              )}

              {columnFlags.dinarNotaVenta && (
                <Column
                  columnKey="espera"
                  header="Espera"
                  style={{ width: "5%" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                />
              )}

              {(columnFlags.dinarNotaVenta || columnFlags.dinarBloqueada) && (
                <Column
                  columnKey="estado"
                  header="Estado"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "5%" }}
                />
              )}

              {columnFlags.showTotal && (
                <Column
                  columnKey="total"
                  header="Total"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "8%" }}
                  body={(rowData) => (
                    <div className="flex flex-column">
                      <span
                        className="white-space-nowrap overflow-hidden text-overflow-ellipsis"
                        style={{ maxWidth: "5rem" }}
                        title={rowData.total}
                      >
                        {Number(rowData.total ?? 0).toLocaleString("es-CL", {
                          style: "currency",
                          currency: "CLP",
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                  )}
                />
              )}

              {columnFlags.dinarBloqueada && (
                <Column
                  columnKey="lineaCredito"
                  header="Línea de Crédito"
                  style={{ width: "9%" }}
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  body={(rowData) =>
                    Number(rowData.lineaCredito ?? 0).toLocaleString("es-CL", {
                      style: "currency",
                      currency: "CLP",
                      maximumFractionDigits: 0,
                    })
                  }
                />
              )}

              {columnFlags.sackCotizacion && (
                <Column
                  columnKey="estado"
                  header="Estado"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "12%" }}
                  body={(rowData) => (
                    <div className="flex">
                      <Button
                        aria-label={rowData.estado}
                        icon="pi pi-cog"
                        tooltip="Cambiar estado"
                        tooltipOptions={{ position: "top" }}
                        text
                        className="p-button-sm p-0"
                        onClick={() => onEstados(rowData)}
                      />
                      <span>{rowData.estado}</span>
                    </div>
                  )}
                />
              )}

              {columnFlags.sackCotizacion && (
                <Column
                  columnKey="motivo"
                  header="Motivo"
                  style={{ width: "10%" }}
                  body={(rowData) =>
                    rowData.estado === "COMPRA ANULADA" ? (
                      <MotivoCell
                        rowData={rowData}
                        causas={causas}
                        glosacausas={glosacausas}
                        setLoading={setLoading}
                        toast={toast}
                        onRefresh={onRefresh}
                      />
                    ) : null
                  }
                />
              )}

              {columnFlags.tiempo && (
                <Column
                  columnKey="inicio"
                  header="Inicio"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "6%" }}
                  body={(rowData) =>
                    formatDate(rowData.inicio ?? rowData.fechaInicio)
                  }
                />
              )}

              {columnFlags.tiempo && (
                <Column
                  columnKey="termino"
                  header="Término"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "6%" }}
                  body={(rowData) =>
                    formatDate(rowData.termino ?? rowData.fechaTermino)
                  }
                />
              )}

              {columnFlags.tiempo && (
                <Column
                  columnKey="tiempo"
                  header="Tiempo"
                  headerClassName="border-right-1 surface-border border-bottom-0"
                  style={{ width: "6%" }}
                  body={(rowData) =>
                    rowData.tiempo ?? rowData.tiempoRespuesta ?? ""
                  }
                />
              )}
            </DataTable>
          </div>
        )}

        {isSack && (
          <div className="col-12 md:col-6 flex align-items-center justify-content-left gap-3 p-2 flex-none">
            <span className="text-600 text-sm">Cambiar estado</span>
            <Dropdown
              value={estadoSeleccionado}
              options={Object.values(
                (estados || []).reduce((acc, item) => {
                  const group = item.tipo || "Otros";
                  if (!acc[group]) acc[group] = { label: group, items: [] };
                  acc[group].items.push(item);
                  return acc;
                }, {}),
              )}
              optionLabel="descripcion"
              optionGroupLabel="label"
              optionGroupChildren="items"
              onChange={(e) => setEstadoSeleccionado(e.value)}
              placeholder="Seleccione estado"
              className="w-20rem p-inputtext-sm "
              showClear
              filter
            />

            <Button
              label="Aplicar"
              className="w-8rem"
              disabled={
                !estadoSeleccionado ||
                !selectedCotizaciones ||
                selectedCotizaciones.length === 0
              }
              onClick={async () => {
                try {
                  setLoading(true);
                  const ids = (selectedCotizaciones || [])
                    .map((c) => c.id)
                    .filter((id) => id != null);
                  await cambiarEstadosCotizacion(
                    ids,
                    estadoSeleccionado.id ?? estadoSeleccionado,
                  );
                  toast.current.show({
                    severity: "success",
                    summary: "Éxito",
                    detail: "Estado(s) actualizado(s) correctamente",
                  });
                  if (onRefresh) onRefresh();
                } catch (error) {
                  console.error(
                    "Error cambiando estados de cotizaciones:",
                    error,
                  );
                  toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail:
                      "Error al cambiar el estado de las cotizaciones seleccionadas",
                  });
                } finally {
                  setLoading(false);
                }
              }}
              icon="pi pi-check"
            />
          </div>
        )}
      </div>

      <EstadosDialog
        visible={visibleEstados}
        onCancel={() => setVisibleEstados(false)}
        estados={estadosData.estados}
        cotizacion={estadosData.cotizacion}
        detalles={estadosData.detalles}
        toast={toast}
        onRefresh={onRefresh}
      />

      <EditarCotizacionDialog
        visible={visibleEditar}
        onCancel={() => setVisibleEditar(false)}
        cotizacion={editarData.cotizacion}
        toast={toast}
      />

      <MailDialog
        visible={visibleEmail}
        onCancel={() => setVisibleEmail(false)}
      />

      <HistoriaDialog
        visible={visibleHistoria}
        onCancel={() => setVisibleHistoria(false)}
        data={historiaData}
      />

      <FasesDialog
        visible={visibleFases}
        onCancel={() => setVisibleFases(false)}
        data={fasesData}
        cotizacion={cotizacionData}
      />

      <DatosDialog
        visible={isDatosDialogVisible}
        onCancel={() => setIsDatosDialogVisible(false)}
        codigo={selectedClienteCodigo}
      />
    </>
  );
};

export default Tabla;
